import { useState } from 'react';
import { Connection } from '../../services/connectionService';
import { sharepointService } from '../../services/sharepointService';
import { useConfirm } from '../../hooks/useConfirm';
import toast from 'react-hot-toast';
import {
  CheckCircleIcon,
  ExclamationTriangleIcon,
  ArrowPathIcon,
  TrashIcon,
  FolderIcon,
} from '@heroicons/react/24/outline';
import { SharePointSetupModal } from './SharePointSetupModal';

interface SharePointConnectionProps {
  connection?: Connection;
  onUpdate: () => void;
}

export function SharePointConnection({ connection, onUpdate }: SharePointConnectionProps) {
  const [showSetupModal, setShowSetupModal] = useState(false);
  const { confirm, ConfirmDialog } = useConfirm();
  
  const handleConnect = async () => {
    try {
      await sharepointService.startOAuthFlow();
    } catch (error) {
      console.error('Failed to start SharePoint OAuth:', error);
      toast.error('Failed to connect to SharePoint');
    }
  };
  
  const handleDisconnect = async () => {
    if (!connection) return;
    
    const confirmed = await confirm({
      title: 'Disconnect SharePoint?',
      message: 'This will remove the connection. Document links will no longer work until you reconnect.',
      confirmText: 'Disconnect',
      cancelText: 'Cancel',
      variant: 'danger',
    });
    
    if (!confirmed) return;
    
    const toastId = toast.loading('Disconnecting...');
    
    try {
      await sharepointService.deleteConnection(connection.id);
      toast.success('SharePoint disconnected', { id: toastId });
      onUpdate();
    } catch (error) {
      console.error('Failed to disconnect:', error);
      toast.error('Failed to disconnect', { id: toastId });
    }
  };
  
  const handleOpenSetup = () => {
    setShowSetupModal(true);
  };

  // Parse connection config for display
  const siteUrl = connection?.config?.site_url as string | undefined;
  const siteName = connection?.config?.site_name as string | undefined;
  const foldersSetup = connection?.config?.folders as Record<string, string> | undefined;

  return (
    <div className="border border-gray-200 rounded-lg p-6 bg-white shadow-sm">
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-cyan-600 rounded-lg flex items-center justify-center">
            <svg className="w-7 h-7 text-white" fill="currentColor" viewBox="0 0 24 24">
              <path d="M0 0h11.377v11.372H0zm12.623 0H24v11.372H12.623zM0 12.623h11.377V24H0zm12.623 0H24V24H12.623z"/>
            </svg>
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-900">SharePoint</h3>
            <p className="text-sm text-gray-500">Document storage and templates</p>
          </div>
        </div>
        
        {connection?.status === 'active' && (
          <div className="flex items-center gap-2 px-3 py-1 bg-green-50 text-green-700 rounded-full text-sm font-medium">
            <CheckCircleIcon className="w-4 h-4" />
            Connected
          </div>
        )}
        {connection?.status === 'error' && (
          <div className="flex items-center gap-2 px-3 py-1 bg-red-50 text-red-700 rounded-full text-sm font-medium">
            <ExclamationTriangleIcon className="w-4 h-4" />
            Error
          </div>
        )}
      </div>

      {/* Connection Details */}
      {connection && siteUrl && (
        <div className="mb-4 p-4 bg-gray-50 rounded-lg space-y-2 text-sm">
          <div className="flex items-center justify-between">
            <span className="text-gray-600 font-medium">Site:</span>
            <a 
              href={siteUrl} 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-blue-600 hover:underline flex items-center gap-1"
            >
              {siteName || 'SharePoint Site'}
              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
              </svg>
            </a>
          </div>
          
          {foldersSetup && (
            <div className="pt-2 border-t border-gray-200">
              <span className="text-gray-600 font-medium block mb-1">Folders:</span>
              <div className="flex flex-wrap gap-2">
                <span className="inline-flex items-center gap-1 px-2 py-1 bg-white border border-gray-200 rounded text-xs">
                  <FolderIcon className="w-3 h-3 text-blue-500" />
                  Templates
                </span>
                <span className="inline-flex items-center gap-1 px-2 py-1 bg-white border border-gray-200 rounded text-xs">
                  <FolderIcon className="w-3 h-3 text-green-500" />
                  Generated Contracts
                </span>
                <span className="inline-flex items-center gap-1 px-2 py-1 bg-white border border-gray-200 rounded text-xs">
                  <FolderIcon className="w-3 h-3 text-purple-500" />
                  Signed Contracts
                </span>
              </div>
            </div>
          )}
          
          {connection.lastSyncedAt && (
            <div className="flex items-center justify-between pt-2 border-t border-gray-200">
              <span className="text-gray-600">Last tested:</span>
              <span className="text-gray-900">
                {new Date(connection.lastSyncedAt).toLocaleString()}
              </span>
            </div>
          )}
        </div>
      )}

      {/* Description when not connected */}
      {!connection && (
        <p className="text-gray-600 mb-4 text-sm">
          Connect to Microsoft SharePoint to store contract templates and generated documents. 
          We'll help you set up the necessary folders in your SharePoint site.
        </p>
      )}

      {/* Actions */}
      <div className="flex gap-3">
        {!connection ? (
          <button
            onClick={handleConnect}
            className="flex-1 inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            Connect to SharePoint
          </button>
        ) : (
          <>
            {connection.status === 'pending_auth' ? (
              <>
                <button
                  onClick={handleOpenSetup}
                  className="flex-1 inline-flex items-center justify-center px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  <FolderIcon className="w-5 h-5 mr-2" />
                  Complete Setup - Configure Site
                </button>
                <button
                  onClick={handleDisconnect}
                  className="inline-flex items-center px-3 py-2 border border-red-300 text-sm font-medium rounded-md text-red-700 bg-white hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                >
                  <TrashIcon className="w-4 h-4 mr-2" />
                  Disconnect
                </button>
              </>
            ) : (
              <>
                <button
                  onClick={handleOpenSetup}
                  disabled={connection.status !== 'active'}
                  className="inline-flex items-center px-3 py-2 border border-blue-300 text-sm font-medium rounded-md text-blue-700 bg-white hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <FolderIcon className="w-4 h-4 mr-2" />
                  Reconfigure Folders
                </button>
                
                <button
                  onClick={handleDisconnect}
                  className="inline-flex items-center px-3 py-2 border border-red-300 text-sm font-medium rounded-md text-red-700 bg-white hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                >
                  <TrashIcon className="w-4 h-4 mr-2" />
                  Disconnect
                </button>
              </>
            )}
          </>
        )}
      </div>
      
      <ConfirmDialog />
      
      {/* Setup Modal */}
      {showSetupModal && connection && (
        <SharePointSetupModal
          isOpen={showSetupModal}
          onClose={() => setShowSetupModal(false)}
          connectionId={connection.id}
          onComplete={() => {
            setShowSetupModal(false);
            onUpdate();
          }}
        />
      )}
    </div>
  );
}
